import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import type { ReviewQueueItem } from '@/api/studio-production'
import { ProtoBadge } from '@/features/studio/components/ui-kit'
import { formatShotSize } from '@/features/studio/shot/storyboard-sheet'
import {
  isReviewAssetItem,
  isReviewShotItem,
  reviewItemTitle,
  reviewStatusTone,
} from '@/features/studio/pages/reviews/review-utils'

type ReviewItemContextBarProps = {
  item: ReviewQueueItem | null
  statusLabel: (item: ReviewQueueItem) => string
  subjectLabel: (item: ReviewQueueItem) => string
  sourceLink: (item: ReviewQueueItem) => { to: string; label: string } | null
}

export function ReviewItemContextBar({
  item,
  statusLabel,
  subjectLabel,
  sourceLink,
}: ReviewItemContextBarProps) {
  const { t } = useTranslation()

  if (!item) {
    return (
      <div className="min-w-0">
        <p className="text-sm font-medium text-zinc-400">{t('reviews.emptySelectionTitle')}</p>
      </div>
    )
  }

  const link = sourceLink(item)
  const metaChips: string[] = []

  if (isReviewShotItem(item)) {
    if ((item.episode_no ?? 0) > 0) {
      metaChips.push(`E${String(item.episode_no).padStart(2, '0')}`)
    }
    if ((item.scene_no ?? 0) > 0 || (item.shot_no ?? 0) > 0) {
      metaChips.push(
        `S${String(item.scene_no ?? 0).padStart(2, '0')}-${String(item.shot_no ?? 0).padStart(2, '0')}`,
      )
    }
    const size = formatShotSize(item.shot_size ?? '')
    if (size) metaChips.push(size)
    if ((item.duration_sec ?? 0) > 0) metaChips.push(`${item.duration_sec}s`)
  }

  if (isReviewAssetItem(item)) {
    metaChips.push(
      t(`assets.kind.${item.asset_kind ?? 'misc'}`, { defaultValue: item.asset_kind ?? '—' }),
    )
  }

  metaChips.push(subjectLabel(item))

  return (
    <div className="review-item-context min-w-0 space-y-1.5">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <h2 className="font-mono text-sm font-medium text-indigo-200">{reviewItemTitle(item)}</h2>
        <ProtoBadge tone={reviewStatusTone(item)} className="text-[10px]">
          {statusLabel(item)}
        </ProtoBadge>
        {link ? (
          <Link to={link.to} className="ml-auto shrink-0 text-[11px] text-indigo-300 hover:text-indigo-200">
            {link.label}
          </Link>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {metaChips.map((chip) => (
          <span
            key={chip}
            className="inline-flex rounded-md border border-amber-400/25 bg-[#12121a] px-1.5 py-0.5 text-[10px] text-zinc-400"
          >
            {chip}
          </span>
        ))}
      </div>
      {(item.description || item.note)?.trim() ? (
        <p className="line-clamp-2 text-[11px] leading-relaxed text-zinc-500">
          {item.description || item.note}
        </p>
      ) : null}
    </div>
  )
}
